// Search-result length rules (owner): a <title> of 30-60 characters, a meta description of 70-160.
// Page copy is written to fit; this is the safety net the edge (functions/_middleware.ts) and the
// browser's <Meta> both apply, so no template can ship a title Google cuts or a snippet it pads.
import { clampDescription } from './tournament-seo.mjs';

export const TITLE_MIN = 30;
export const TITLE_MAX = 60;
export const DESC_MIN = 70;
export const DESC_MAX = 160;

const BRAND = /\s*[|—-]\s*TixInfo\s*$/;
const BRAND_TAIL = ' | TixInfo';
const fits = (s) => s.length >= TITLE_MIN && s.length <= TITLE_MAX;
// Words a cut title may not end on, because it would read as an unfinished sentence ("Tickets from", "Dates and",
// "Cavaliers Tickets Range"): articles, prepositions, conjunctions, linking and modal verbs, "like", "range".
const DANGLING =
  'a|about|above|across|after|against|along|among|an|and|are|around|as|at|because|before|behind|below|between|beyond|but|by|can|could|despite|did|does|during|for|from|had|has|have|her|his|how|if|in|into|is|its|like|may|might|my|near|nor|of|on|onto|or|our|over|per|performing|presents|range|ranges|ranging|should|since|than|that|the|their|then|these|this|those|through|to|toward|towards|under|until|upon|via|vs\\.?|was|were|what|when|where|whether|which|while|who|whose|why|will|with|within|without|would|your|&|&amp;';
const DANGLING_END = new RegExp(`(?:\\s+(?:${DANGLING}))+$`, 'i');
/** no cut title ends on a joining word, a possessive or a separator ("Tickets from", "Dates and", "Layout &",
 *  "Cavaliers Tickets Range", "Why the Cavaliers's")
 *  @param {string} s */
export const trimDangling = (s) => {
  let t = String(s);
  for (let i = 0; i < 8; i++) {
    const before = t;
    t = t
      .replace(/[\s,;:\-–—]+$/, '')
      .replace(DANGLING_END, '')
      .replace(/\s+[\p{L}\d.&-]+['’]s$/u, '') // a possessive with nothing after it: "the Team's"
      .replace(/\s+\p{L}+s['’]$/u, '') // …or a plural one: "the Cavaliers'"
      .replace(/[\s,;:\-–—]+$/, '');
    if (t === before) break;
  }
  return t;
};

/** A title of at most 60 characters, cut where a reader would: the brand suffix, then trailing " — "
 *  parts, then an aside in brackets, then a long name's price clause, then everything after the last
 *  comma, colon or dash that still leaves 30, then a word. No "…": the SEO crawl of 2026-10-23 found
 *  455 titles shown cut mid-phrase ("Center Tickets from $5…", "HelloTickets vs…").
 *  A title that says "Unofficial" (event pages: the resale disclaimer) is never shortened.
 *  @param {string} title */
export const fitTitle = (title) => {
  const t = String(title ?? '').replace(/\s+/g, ' ').trim();
  if (t.length <= TITLE_MAX || /unofficial/i.test(t)) return t;
  const bare = t.replace(BRAND, '');
  if (bare.length <= TITLE_MAX) return bare;
  const parts = bare.split(/ [—|] /);
  while (parts.length > 1 && parts.join(' — ').length > TITLE_MAX) parts.pop();
  let head = parts.join(' — ');
  if (fits(head)) return head;
  if (head.length < TITLE_MIN) head = bare;
  // "Madison Square Garden Seating Capacity (≈20,500), Layout & Views"
  head = head.replace(/\s*\([^)]*\)/g, '');
  if (fits(head)) return head;
  // "Thompson Boling Arena at Food City Center Tickets from $5.75 all-in": the name and "Tickets" stay whole
  head = head.replace(/\s+from\s+\$[\d.,]+(?:\s+(?:all-in|\+ fees))?/, '');
  if (fits(head)) return head;
  // "Which Ticket Site Is Cheapest? HelloTickets vs TicketNetwork vs Ticket Squeeze": a whole clause, with the brand
  // back on when the title had it and there is room (a clause with the brand needs only 30 - 10 characters of its own,
  // so "Same City, Two NBA Nights: Why Cavaliers Tickets Range …" now ends at "Same City, Two NBA Nights | TixInfo").
  // A clause beats a word cut: the longest clause that fits wins.
  const branded = BRAND.test(t);
  const room = (c) => (branded ? `${c}${BRAND_TAIL}` : c);
  let clause = '';
  for (const m of head.matchAll(/[,;:](?=\s)|\s[–—-]\s|[?!](?=\s)/g)) {
    const c = trimDangling(head.slice(0, /[?!]/.test(m[0]) ? m.index + 1 : m.index));
    if (fits(room(c)) && c.length > clause.length) clause = c;
  }
  if (clause) return room(clause);
  // no clause fits: a word cut keeps as much of the name as fits, without the brand (a venue's name stays whole)
  const words = trimDangling(head.slice(0, head.lastIndexOf(' ', TITLE_MAX)));
  return words.length >= TITLE_MIN ? words : head.slice(0, TITLE_MAX).trim();
};

/** A description of 70-160 characters: cut at a sentence or word when long, a plain site sentence added when short.
 *  @param {string} description */
export const fitDescription = (description) => {
  let d = clampDescription(description, DESC_MAX);
  if (d.length < DESC_MIN) {
    const pad = ' Compare partner marketplace prices with every fee labeled on TixInfo.';
    d = `${d}${/[.!?…]$/.test(d) ? '' : '.'}${pad}`;
    if (d.length > DESC_MAX) d = clampDescription(d, DESC_MAX);
  }
  return d;
};
